import { Logo } from '@/components/layout/dashboard-layout'
import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useAuth } from '@workos-inc/authkit-react'
import { Check, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useCreateCheckout,
  useGetOAuthUrl,
  useInboxes,
  useRedeemPromoCode,
  useSubscription,
  useValidatePromoCode,
  type OnboardingStatus,
} from '@/api'
import {
  ClassificationPanel,
  type MailboxSection,
} from '@/components/classification/classification-panel'
import { StepSubscribe } from './step-subscribe'
import { StepConnect } from './step-connect'

const steps = [
  'Connect',
  'Instructions',
  'Actions',
  'Preview',
  'Subscription',
  'Enable',
]
const sections: Record<number, MailboxSection> = {
  1: 'instructions',
  2: 'permissions',
  3: 'preview',
  5: 'processing',
}

export function OnboardingWizard({
  initialStatus,
}: {
  initialStatus: OnboardingStatus
}) {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/onboarding' })
  const inboxes = useInboxes()
  const oauth = useGetOAuthUrl()
  const checkout = useCreateCheckout()
  const redeem = useRedeemPromoCode()
  const subscription = useSubscription({ staleTime: 0 })
  const [error, setError] = useState('')
  const [current, setUserStep] = useState(() =>
    !initialStatus.has_inbox
      ? 0
      : search.manual === 'billing'
        ? initialStatus.has_subscription
          ? 5
          : 4
        : search.subscription === 'success'
          ? 4
          : !initialStatus.has_instruction
            ? 1
            : !initialStatus.has_custom_permissions
              ? 2
              : !initialStatus.has_subscription
                ? 4
                : 5,
  )
  const [dirty, setDirty] = useState(false)
  const onDirtyChange = useCallback(
    (_id: string, value: boolean) => setDirty(value),
    [],
  )
  const inbox =
    inboxes.data?.find((item) => item.uuid === search.account) ??
    inboxes.data?.find((item) => item.uuid === initialStatus.inbox_uuid) ??
    inboxes.data?.[0]
  const promo = useMemo(
    () =>
      search.code ||
      sessionStorage.getItem('onboarding_promo_code') ||
      undefined,
    [search.code],
  )
  const validation = useValidatePromoCode(promo)
  const connect = async (provider: 'google' | 'microsoft') => {
    setError('')
    try {
      const result = await oauth.mutateAsync({
        provider,
        returnTo: '/onboarding',
      })
      window.location.href = result.url
    } catch {
      setError('The connection could not start. Try again.')
    }
  }
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between p-4">
          <Logo />
          <Button variant="ghost" size="sm" onClick={() => signOut()}>
            <LogOut className="size-4" />
            <span className="hidden sm:inline">Log out</span>
          </Button>
        </div>
      </header>
      {current !== 0 && search.manual !== 'billing' && (
        <div className="border-b bg-muted/30">
          <nav
            aria-label="Setup progress"
            className="mx-auto max-w-5xl px-4 py-4"
          >
            <p className="text-sm font-medium md:hidden">
              Step {current + 1} of {steps.length} · {steps[current]}
            </p>
            <ol className="hidden items-center justify-between gap-3 md:flex">
              {steps.map((step, index) => (
                <li
                  key={step}
                  aria-current={current === index ? 'step' : undefined}
                  className="flex items-center gap-2 text-sm"
                >
                  <span
                    className={`flex size-8 items-center justify-center rounded-full ${index <= current ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                  >
                    {index < current ? <Check className="size-4" /> : index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      )}
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-8">
        {(search.success === 'false' || search.error) && (
          <p role="alert" className="text-sm text-destructive">
            The mailbox connection was not completed. Try connecting again.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {current === 0 ? (
          <StepConnect onConnect={connect} isLoading={oauth.isPending} />
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {steps[current]}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {inbox?.display_name ?? initialStatus.inbox_email}
              </p>
            </div>
            {inbox && (
              <div hidden={current === 4}>
                <ClassificationPanel
                  inboxId={inbox.uuid}
                  gmail={inbox.account_type === 'google_email'}
                  section={sections[current]}
                  starterInstructions
                  onDirtyChange={onDirtyChange}
                />
              </div>
            )}
            {inboxes.isLoading && <p role="status">Loading your mailbox…</p>}
            {inboxes.isError && (
              <div role="alert">
                <p>Your mailbox could not load.</p>
                <Button variant="outline" onClick={() => inboxes.refetch()}>
                  Try again
                </Button>
              </div>
            )}
            {current === 4 &&
              (subscription.data?.has_subscription ? (
                <div className="space-y-4">
                  <p>Your subscription is active.</p>
                  <Button onClick={() => setUserStep(5)}>
                    Continue to enable
                  </Button>
                </div>
              ) : (
                <StepSubscribe
                  onSubscribe={async (tier) => {
                    setError('')
                    try {
                      const result = await checkout.mutateAsync({
                        tier,
                        successUrl: `${window.location.origin}/onboarding?subscription=success&account=${inbox?.uuid ?? ''}`,
                        cancelUrl: `${window.location.origin}/onboarding?account=${inbox?.uuid ?? ''}`,
                      })
                      window.location.href = result.checkout_url
                    } catch {
                      setError('Checkout could not start. Try again.')
                    }
                  }}
                  onRedeemPromo={async () => {
                    if (!promo) return
                    setError('')
                    try {
                      await redeem.mutateAsync(promo)
                      await subscription.refetch()
                      await navigate({
                        to: '/onboarding',
                        search: {
                          account: inbox?.uuid,
                          subscription: 'success',
                        },
                        replace: true,
                      })
                      sessionStorage.removeItem('onboarding_promo_code')
                      setUserStep(5)
                    } catch {
                      setError(
                        'The code could not be redeemed. Check it and try again.',
                      )
                    }
                  }}
                  isLoading={checkout.isPending}
                  promoValidation={validation.data}
                  promoLoading={validation.isLoading}
                  promoRedeeming={redeem.isPending}
                  promoError={error}
                />
              ))}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-6">
              <Button
                variant="outline"
                disabled={dirty || current <= 1}
                onClick={() => setUserStep(current - 1)}
              >
                Back
              </Button>
              {current < 4 && (
                <Button
                  disabled={dirty || !inbox}
                  onClick={() => setUserStep(current + 1)}
                >
                  Continue
                </Button>
              )}
              {current === 5 && (
                <Button variant="outline" asChild>
                  <Link to="/setup">
                    Return to setup
                  </Link>
                </Button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
